import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { Formik, Form, Field, FieldArray } from 'formik';
import { Button, IconButton, InputAdornment } from '@mui/material';
import { BarChart, Edit } from '@mui/icons-material';

import biService from '../../../service/bi.service';
import dashboardService from '../../../service/dashboard.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import Yup from '../../../utils/yup';
import {
  InputUI,
  AutoCompleteSelectUI,
  FormHeaderUI,
  FormLabelUI,
  FormErrorUI,
  FormButtonPanelUI,
  SelectUI,
  CheckboxUI,
  DeleteDialogUI,
  DialogUI,
  FormBase,
} from '../../Interface';
import DashboardReports from './DashboardReports';
import BiReport from '../BiReport/BiReport';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import { DashboardTypeList } from '../../../config/dashboard';
import {
  compactDashboardReports,
  createEmptyDashboardCell,
  expandDashboardReports,
  getDashboardCellLayout,
} from '../../../utils/dashboardLayout';

const DashboardLayoutEditor = ({
  values,
  errors,
  validateField,
  setFieldValue,
  disabled,
  autocompleteData,
  previewMode,
}) => {
  const [conflictingIndexes, setConflictingIndexes] = useState([]);
  const [gridHeight, setGridHeight] = useState(0);
  const gridRef = useRef(null);

  useEffect(() => {
    setConflictingIndexes([]);
  }, [values.type]);

  useLayoutEffect(() => {
    if (!gridRef.current) return undefined;

    const updateGridHeight = () => setGridHeight(gridRef.current?.clientHeight ?? 0);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateGridHeight) : null;

    updateGridHeight();
    resizeObserver?.observe(gridRef.current);
    window.addEventListener('resize', updateGridHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateGridHeight);
    };
  }, [values.type, previewMode]);

  const dashboardType = DashboardTypeList.find((type) => type.value === values.type);

  if (!dashboardType) return null;

  const layout = getDashboardCellLayout(values.dashboardReport, dashboardType.column, dashboardType.row);
  const gridGap = 6;
  const availableHeight = Math.max(1, gridHeight);
  const rowHeight = Math.max(1, (availableHeight - (dashboardType.row - 1) * gridGap) / dashboardType.row);

  const rectanglesOverlap = (first, second) =>
    first.column < second.column + second.width &&
    first.column + first.width > second.column &&
    first.row < second.row + second.height &&
    first.row + first.height > second.row;

  const changeReportSize = (index, dimension, value, row, column) => {
    const report = values.dashboardReport[index] ?? createEmptyDashboardCell();
    const maximum = dimension === 'width' ? dashboardType.column - column + 1 : dashboardType.row - row + 1;
    const nextValue = Math.max(1, Math.min(Number(value), maximum));
    const nextRectangle = {
      row,
      column,
      width: dimension === 'width' ? nextValue : Number(report.width) || 1,
      height: dimension === 'height' ? nextValue : Number(report.height) || 1,
    };
    const conflicts = layout.filter(
      (cell) =>
        !cell.covered &&
        cell.index !== index &&
        values.dashboardReport[cell.index]?.biReportId &&
        rectanglesOverlap(nextRectangle, cell)
    );

    if (conflicts.length > 0) {
      setConflictingIndexes(conflicts.map((cell) => cell.index));
      addNotification({
        content: 'A megadott riportméret egy már foglalt rácselemmel ütközik.',
        type: NotificationType.ERROR,
      });
      return;
    }

    setConflictingIndexes([]);
    setFieldValue(`dashboardReport.${index}.${dimension}`, nextValue);
  };

  return (
    <div ref={gridRef} className="h-full min-h-0 w-full">
      <div
        className="grid h-full min-h-0"
        style={{
          gap: `${gridGap}px`,
          gridTemplateColumns: `repeat(${dashboardType.column}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${dashboardType.row}, minmax(0, 1fr))`,
        }}
      >
        {layout.map(({ index, row, column, covered, width, height }) => {
          if (covered) return null;

          const report = values.dashboardReport[index] ?? createEmptyDashboardCell();
          const cellHeight = height * rowHeight + (height - 1) * gridGap;
          const minimumReportHeight = 180;
          const previewAvailableHeight = Math.max(1, cellHeight - 8);
          const previewScale = Math.max(0.15, Math.min(1, previewAvailableHeight / minimumReportHeight));
          const editorPreviewHeight = Math.max(1, cellHeight - 108);
          const editorPreviewScale = Math.max(0.15, Math.min(1, editorPreviewHeight / minimumReportHeight));
          const reportScale = previewMode ? previewScale : editorPreviewScale;
          const reportHeight = previewMode
            ? Math.max(minimumReportHeight, previewAvailableHeight / reportScale)
            : minimumReportHeight;

          return (
            <div
              key={`${row}-${column}`}
              className={`flex min-h-0 flex-col overflow-hidden rounded-lg border bg-white shadow-sm ${
                previewMode ? 'p-0' : 'p-2'
              } ${
                conflictingIndexes.includes(index) ? 'border-red-500 ring-2 ring-red-500' : 'border-slate-200'
              }`}
              style={{
                gridColumn: `${column} / span ${width}`,
                gridRow: `${row} / span ${height}`,
              }}
            >
              {!previewMode && (
                <>
                  <div className="mb-1 text-xs font-semibold text-labelColor">
                    {row}. sor / {column}. oszlop
                  </div>
                  <Field
                    type="autocomplete"
                    name={`dashboardReport.${index}.biReportId`}
                    component={AutoCompleteSelectUI}
                    fullWidth
                    label={<FormLabelUI text="Riport" />}
                    variant="standard"
                    helperText={<FormErrorUI message={errors.dashboardReport?.[index]?.biReportId} />}
                    inputLabelProps={{ shrink: report.biReportId !== null }}
                    onChange={(_event, newValue) => {
                      setConflictingIndexes([]);
                      setFieldValue(`dashboardReport.${index}.biReportId`, newValue?.value ?? null).then(() => {
                        validateField(`dashboardReport.${index}.biReportId`);
                      });
                    }}
                    disabled={disabled}
                    selectedValue={report.biReportId ?? null}
                    selectedLabelValue={
                      autocompleteData.biReports.find((item) => item.value === report.biReportId)?.label ?? ''
                    }
                    dataset={autocompleteData.biReports}
                    isOptionEqualToValue={(option, value) => option.value === value}
                  />
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <Field
                      type="number"
                      name={`dashboardReport.${index}.width`}
                      as={InputUI}
                      fullWidth
                      disabled={disabled}
                      label={<FormLabelUI text="Szélesség" />}
                      variant="standard"
                      inputProps={{ min: 1, max: dashboardType.column - column + 1 }}
                      onChange={(event) => changeReportSize(index, 'width', event.target.value, row, column)}
                    />
                    <Field
                      type="number"
                      name={`dashboardReport.${index}.height`}
                      as={InputUI}
                      fullWidth
                      disabled={disabled}
                      label={<FormLabelUI text="Magasság" />}
                      variant="standard"
                      inputProps={{ min: 1, max: dashboardType.row - row + 1 }}
                      onChange={(event) => changeReportSize(index, 'height', event.target.value, row, column)}
                    />
                  </div>
                </>
              )}
              {report.biReportId && (
                <div className={`${previewMode ? 'h-full' : 'mt-1 min-h-0 flex-1'} overflow-hidden`}>
                  <div
                    style={{
                      transform: `scale(${reportScale})`,
                      transformOrigin: 'top left',
                      width: `${100 / reportScale}%`,
                    }}
                  >
                    <BiReport
                      reportId={report.biReportId}
                      height={reportHeight}
                      filter={{ interval: { from: null, to: null } }}
                      dashboardData={values}
                      dashboardView
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DashboardAdmin = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [addField, setAddField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(true);
  const [viewField, setViewField] = useState(true);
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [initValues, setInitValues] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLayoutDialog, setShowLayoutDialog] = useState(false);
  const [layoutPreviewMode, setLayoutPreviewMode] = useState(false);

  const [sourceData, setSourceData] = useState({
    biReports: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({
    biReports: [],
  });

  useEffect(() => {
    getMenus();
    getAllReports();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('dashboardAdmin');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        valuesToForm();
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    setAutocompleteData((prev) => {
      const newAutocompleteData = { ...prev };

      if (!isEqual(sourceData.biReports, prev.biReports)) {
        newAutocompleteData.biReports = [];
        sourceData.biReports.forEach((biReport) => {
          newAutocompleteData.biReports.push({
            value: biReport.id,
            label: biReport.name,
          });
        });
      }

      return newAutocompleteData;
    });
  }, [sourceData]);

  const getAllReports = () => {
    biService.allBiReports().then((data) =>
      setSourceData((prev) => {
        return { ...prev, biReports: data };
      })
    );
  };

  const handleSubmit = (values) => {
    const dashboardType = DashboardTypeList.find((type) => type.value === values.type);
    const data = {
      ...values,
      custom1Filter: Boolean(values.custom1Filter),
      custom1FilterLabel: values.custom1Filter ? values.custom1FilterLabel : null,
      refreshMin: values.refreshMin === '' ? null : values.refreshMin,
      ...(!values.id ? { createdBy: user.userId } : { updatedBy: user.userId }),
      dashboardReport: dashboardType
        ? compactDashboardReports(values.dashboardReport, dashboardType.column, dashboardType.row)
        : [],
    };

    dashboardService.upsertDashboard(data).then((data) => {
      setRefetchTable(true);
      setViewField(true);
      setEditButtonvisible(true);
      setAddField(true);
      setShowLayoutDialog(false);
      setLayoutPreviewMode(false);
    });
  };

  const valuesToForm = () => {
    const dashboardType = DashboardTypeList.find((type) => type.value === selectedValues.type);
    setInitValues({
      ...selectedValues,
      custom1Filter: selectedValues.custom1Filter ?? false,
      custom1FilterLabel: selectedValues.custom1FilterLabel ?? '',
      refreshMin: selectedValues.refreshMin ?? '',
      dashboardReport: dashboardType
        ? expandDashboardReports(selectedValues.dashboardReport, dashboardType.column, dashboardType.row)
        : [],
    });
  };

  const defaultValues = useMemo(
    () => ({
      name: '',
      type: null,
      bpFilter: false,
      custom1Filter: false,
      custom1FilterLabel: '',
      yearFilter: false,
      halfYearFilter: false,
      quarterFilter: false,
      weekFilter: false,
      monthFilter: false,
      intervalFilter: false,
      refreshMin: '',
      dashboardReport: [],
    }),
    []
  );

  const validationSchema = Yup.object().shape({
    id: Yup.number().nullable(),
    name: Yup.string().required(),
    type: Yup.string().required(),
    bpFilter: Yup.boolean(),
    custom1Filter: Yup.boolean(),
    custom1FilterLabel: Yup.string()
      .trim()
      .nullable()
      .when('custom1Filter', {
        is: true,
        then: (schema) => schema.required(),
      }),
    yearFilter: Yup.boolean(),
    halfYearFilter: Yup.boolean(),
    quarterFilter: Yup.boolean(),
    weekFilter: Yup.boolean(),
    monthFilter: Yup.boolean(),
    intervalFilter: Yup.boolean(),
    refreshMin: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .integer()
      .positive()
      .nullable(),
    dashboardReport: Yup.array()
      .of(
        Yup.object().shape({
          biReportId: Yup.string().nullable(),
          width: Yup.number().integer().min(1).required(),
          height: Yup.number().integer().min(1).required(),
        })
      )
      .test('required-dashboard-reports', function (reports) {
        const dashboardType = DashboardTypeList.find((type) => type.value === this.parent.type);
        if (!dashboardType) return true;

        const emptyCell = getDashboardCellLayout(reports, dashboardType.column, dashboardType.row).find(
          ({ index, covered }) => !covered && !reports?.[index]?.biReportId
        );

        return emptyCell
          ? this.createError({
              path: `${this.path}.${emptyCell.index}.biReportId`,
              message: 'A riport kiválasztása kötelező.',
            })
          : true;
      }),
  });

  const generateRows = (value, setFieldValue) => {
    const findedType = DashboardTypeList.find((dt) => value === dt.value);

    if (findedType) {
      const rowCount = findedType.column * findedType.row;
      const rowArray = [];

      for (let i = 0; i < rowCount; i++) {
        rowArray.push(createEmptyDashboardCell());
      }

      return setFieldValue('dashboardReport', rowArray);
    }

    return setFieldValue('dashboardReport', []);
  };

  const closeEditor = () => {
    setViewField(true);
    setEditButtonvisible(true);
    setAddField(true);
    setShowLayoutDialog(false);
    setLayoutPreviewMode(false);
  };

  const handleRemoveElement = async () => {
    dashboardService.deleteDashboard(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
    });
  };

  return (
    <>
      <Formik
        initialValues={initValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, validateField, setFieldValue, resetForm }) => (
          <>
            <FormBase
              name="Dashboard"
              leftPanelContent={
                <div className="h-full overflow-auto custom-scrollbar">
                  <FormHeaderUI
                    addClick={() => {
                      setSelectedValues({});
                      setInitValues(defaultValues);
                      setViewField(false);
                      setAddField(false);
                    }}
                    addDisabled={!viewField || !canEdit}
                    modClick={() => {
                      setViewField(false);
                      setEditButtonvisible(false);
                    }}
                    modDisabled={!viewField || operatorsDisabled || !canEdit}
                    deleteClick={() => {
                      setShowDeleteDialog(true);
                    }}
                    deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                  />
                  <Form>
                    <div className="grid grid-cols-4 gap-4 mx-4 pb-6">
                    <div className="col-span-4">
                      <Field
                        type="text"
                        name="name"
                        as={InputUI}
                        fullWidth
                        disabled={viewField}
                        label={<FormLabelUI text="Alias név" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.name} />}
                        InputLabelProps={{ shrink: values.name !== '' }}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="bpFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Üp. szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-3">
                      <Field
                        name="responsibleFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Felelős szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1">
                      <Field
                        name="custom1Filter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Custom1 szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-3">
                      <Field
                        type="text"
                        name="custom1FilterLabel"
                        as={InputUI}
                        fullWidth
                        disabled={viewField || !values.custom1Filter}
                        label={<FormLabelUI text="Custom1 szűrő felirata" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.custom1FilterLabel} />}
                        InputLabelProps={{ shrink: Boolean(values.custom1FilterLabel) }}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="yearFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Év szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="quarterFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Negyedév szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="halfYearFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Félév szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="monthFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Hónap szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="weekFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Hét szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        name="intervalFilter"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Intervallum szűrő" />}
                        disabled={viewField}
                      />
                    </div>
                    <div className="col-span-1 ">
                      <Field
                        type="number"
                        name="refreshMin"
                        as={InputUI}
                        value={values.refreshMin ?? ''}
                        fullWidth
                        disabled={viewField}
                        label={<FormLabelUI text="Frissítés" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.refreshMin} />}
                        InputLabelProps={{ shrink: values.refreshMin && values.refreshMin !== '' }}
                        inputProps={{ min: 1 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">perc</InputAdornment> }}
                      />
                    </div>
                    <div className="col-span-4">
                      <Button
                        type="button"
                        variant="contained"
                        disabled={viewField}
                        onClick={() => {
                          setLayoutPreviewMode(false);
                          setShowLayoutDialog(true);
                        }}
                      >
                        Rács szerkesztése
                      </Button>
                      <FormErrorUI message={errors?.type} />
                    </div>
                    </div>
                    {(!editButtonVisible || !addField) && (
                      <FormButtonPanelUI
                        onCancelClick={() => {
                          closeEditor();
                          resetForm();
                        }}
                      />
                    )}
                  </Form>
                </div>
              }
              rightPanelContent={
                <DashboardReports
                  refetch={{ refetchTable, setRefetchTable }}
                  values={{ selectedValues, setSelectedValues }}
                  viewField={viewField}
                  operatorsDisabled={operatorsDisabled}
                />
              }
            />
            <DialogUI
              open={showLayoutDialog}
              onClose={() => {
                setShowLayoutDialog(false);
                setLayoutPreviewMode(false);
              }}
              headerContent="Dashboard rács szerkesztése"
              fullScreen
              closeBtn
              contentStyle={{
                backgroundColor: '#f3f4f6',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '8px',
              }}
            >
              <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
                <IconButton
                  title={layoutPreviewMode ? 'Szerkesztőmezők megjelenítése' : 'Dashboard előnézet'}
                  aria-label={layoutPreviewMode ? 'Szerkesztőmezők megjelenítése' : 'Dashboard előnézet'}
                  onClick={() => setLayoutPreviewMode((previous) => !previous)}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: 4,
                    zIndex: 10,
                    backgroundColor: 'white',
                    boxShadow: 2,
                    '&:hover': { backgroundColor: 'grey.100' },
                  }}
                >
                  {layoutPreviewMode ? <Edit /> : <BarChart />}
                </IconButton>
                {!layoutPreviewMode && (
                  <div className="mb-2 max-w-sm shrink-0 rounded-lg bg-white p-2 shadow-sm">
                    <Field
                      name="type"
                      component={SelectUI}
                      fullWidth
                      option={DashboardTypeList}
                      value={values.type ?? ''}
                      variant="standard"
                      onChange={async (event) => {
                        await setFieldValue('type', event.target.value ?? null);
                        await validateField('type');
                        generateRows(event.target.value, setFieldValue);
                      }}
                      disabled={viewField}
                      label={<FormLabelUI text="Rács mérete" />}
                      helperText={<FormErrorUI message={errors?.type} />}
                      InputLabelProps={{ shrink: values.type !== '' }}
                    />
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-hidden">
                  <FieldArray name="dashboardReport">
                    <DashboardLayoutEditor
                      values={values}
                      errors={errors}
                      validateField={validateField}
                      setFieldValue={setFieldValue}
                      disabled={viewField}
                      autocompleteData={autocompleteData}
                      previewMode={layoutPreviewMode}
                    />
                  </FieldArray>
                </div>
              </div>
            </DialogUI>
          </>
        )}
      </Formik>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default DashboardAdmin;
